import { Select, Stack, Tabs } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconSettings } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { parse } from 'qs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearch } from 'wouter'
import { navigate } from 'wouter/use-browser-location'

import { Screen } from '@/components/Screen'
import { type TabKey, TabKeys } from '@/constants/TabKeys'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useTabNavigationWarning } from '@/hooks/useTabNavigationWarning'
import { getRoutePath } from '@/utils/navigation'

// Settings screen specific tab keys
type SettingsTabKey =
  'topics' | 'terminology' | 'sidekicks' | 'factFields' | 'draftWelcomeMessage' | 'generalInformation'

import { BrandingPanel } from './BrandingPanel'
import { DraftWelcomeMessagePanel } from './DraftWelcomeMessagePanel'
import { EditSidekicksPanel } from './EditSidekicksPanel'
import { FactFieldsPanel } from './FactFieldsPanel'
import { GeneralInfoPanel } from './GeneralInfoPanel'
import { notificationsEnabled } from './notifications'
import { NotificationsPanel } from './NotificationsPanel'
import { TopicsPanel } from './TopicsPanel'

export const SettingsScreen = observer(function SettingsScreen() {
  const [activeTab, setActiveTab] = useState<TabKey>(TabKeys.draftWelcomeMessage)
  const searchParamsString = useSearch()
  const searchParams: { tab?: string } = parse(searchParamsString)
  const isMobile = useCheckMobileScreen()
  const showNotifications = notificationsEnabled()

  // Dropdown options for mobile view
  const tabOptions = [
    { value: TabKeys.draftWelcomeMessage, label: 'Welcome Message' },
    { value: TabKeys.generalInformation, label: 'General Information' },
    { value: TabKeys.sidekicks, label: 'Personal Sidekick' },
    { value: TabKeys.factFields, label: 'User Profile' },
    { value: TabKeys.terminology, label: 'Channel Branding' },
    { value: TabKeys.topics, label: 'Topics' },
    ...(showNotifications ? [{ value: TabKeys.notifications, label: 'Notifications' }] : []),
  ]

  // Store functions to check for unsaved changes in each panel
  const unsavedChangesCheckersRef = useRef<Record<SettingsTabKey, (() => boolean) | undefined>>({
    topics: undefined,
    terminology: undefined,
    sidekicks: undefined,
    factFields: undefined,
    draftWelcomeMessage: undefined,
    generalInformation: undefined,
  })

  const discardHandlersRef = useRef<Record<SettingsTabKey, (() => void) | undefined>>({
    topics: undefined,
    terminology: undefined,
    sidekicks: undefined,
    factFields: undefined,
    draftWelcomeMessage: undefined,
    generalInformation: undefined,
  })

  // Function for panels to register their unsaved changes checker
  const registerUnsavedChangesChecker = useCallback(
    (tabKey: SettingsTabKey, checker: () => boolean, onDiscard: () => void) => {
      unsavedChangesCheckersRef.current[tabKey] = checker
      discardHandlersRef.current[tabKey] = onDiscard
    },
    [],
  )

  // Set active tab from URL parameter if provided.
  // Access (and 'webSettings', absorbed into it earlier) moved to its own page.
  useEffect(() => {
    if (!searchParams.tab) return
    if (searchParams.tab === TabKeys.access || searchParams.tab === 'webSettings') {
      navigate(getRoutePath('access'), { replace: true })
      return
    }
    const requested = searchParams.tab as TabKey
    if (requested === TabKeys.notifications && !showNotifications) return
    if (Object.values(TabKeys).includes(requested)) {
      setActiveTab(requested)
    }
  }, [searchParams.tab, showNotifications])

  const handleTabChange = useCallback(
    async (value: string | null) => {
      if (!value || !Object.values(TabKeys).includes(value as TabKey)) {
        return
      }

      const newTab = value as TabKey

      // Convert TabKey to SettingsTabKey for lookup
      const getSettingsTabKey = (tabKey: TabKey): SettingsTabKey | null => {
        switch (tabKey) {
          case TabKeys.topics:
            return 'topics'
          case TabKeys.terminology:
            return 'terminology'
          case TabKeys.sidekicks:
            return 'sidekicks'
          case TabKeys.factFields:
            return 'factFields'
          case TabKeys.draftWelcomeMessage:
            return 'draftWelcomeMessage'
          case TabKeys.generalInformation:
            return 'generalInformation'
          default:
            return null
        }
      }

      const currentSettingsTab = getSettingsTabKey(activeTab)
      if (!currentSettingsTab) {
        setActiveTab(newTab)
        return
      }

      const currentTabChecker = unsavedChangesCheckersRef.current[currentSettingsTab]
      const currentTabDiscard = discardHandlersRef.current[currentSettingsTab]

      // Check if current tab has unsaved changes
      if (currentTabChecker && currentTabChecker()) {
        // Show confirmation modal
        let resolveFn: ((value: boolean) => void) | undefined
        const confirmation = new Promise<boolean>(resolve => {
          resolveFn = resolve
        })

        modals.openConfirmModal({
          title: 'Unsaved Changes',
          centered: true,
          children: `You have unsaved changes in the ${getTabDisplayName(currentSettingsTab)} tab. Are you sure you want to leave?`,
          labels: { confirm: 'Leave Tab', cancel: 'Stay' },
          confirmProps: { color: 'red' },
          groupProps: { mt: 'xl' },
          onConfirm: () => {
            currentTabDiscard?.()
            resolveFn?.(true)
          },
          onCancel: () => resolveFn?.(false),
        })

        const confirmed = await confirmation
        if (!confirmed) {
          return // Don't change tabs
        }
      }

      setActiveTab(newTab)
    },
    [activeTab],
  )

  // Helper function to get display name for tab
  const getTabDisplayName = (tabKey: SettingsTabKey): string => {
    switch (tabKey) {
      case 'topics':
        return 'Topics'
      case 'terminology':
        return 'Channel Branding'
      case 'sidekicks':
        return 'Personal Sidekick'
      case 'factFields':
        return 'User Profile'
      case 'draftWelcomeMessage':
        return 'Welcome Message'
      case 'generalInformation':
        return 'General Information'
    }
  }

  // Check if any panel has unsaved changes (for router navigation)
  const hasAnyUnsavedChanges = useCallback((): boolean => {
    return Object.values(unsavedChangesCheckersRef.current).some(checker => checker && checker())
  }, [])

  // Handle discarding changes for all panels (for router navigation)
  const discardAllChanges = useCallback(() => {
    Object.values(discardHandlersRef.current).forEach(handler => handler && handler())
  }, [])

  // Set up router navigation warning (for leaving the entire page)
  useTabNavigationWarning({
    hasUnsavedChanges: hasAnyUnsavedChanges,
    onDiscard: discardAllChanges,
    message: 'You have unsaved changes in your settings. Are you sure you want to leave this page?',
    title: 'Unsaved Settings Changes',
  })

  return (
    <Screen title="Settings" icon={IconSettings}>
      <Stack gap="md">
        {isMobile ? (
          <>
            <Select
              value={activeTab}
              onChange={handleTabChange}
              data={tabOptions}
              size="lg"
              fw={500}
              placeholder="Select a settings section"
            />
          </>
        ) : (
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tabs.List>
              <Tabs.Tab value={TabKeys.draftWelcomeMessage} fw={500} fz="lg">
                Welcome Message
              </Tabs.Tab>
              <Tabs.Tab value={TabKeys.generalInformation} fw={500} fz="lg">
                General Information
              </Tabs.Tab>
              <Tabs.Tab value={TabKeys.sidekicks} fw={500} fz="lg">
                Personal Sidekick
              </Tabs.Tab>
              <Tabs.Tab value={TabKeys.factFields} fw={500} fz="lg">
                User Profile
              </Tabs.Tab>
              <Tabs.Tab value={TabKeys.terminology} fw={500} fz="lg">
                Channel Branding
              </Tabs.Tab>
              <Tabs.Tab value={TabKeys.topics} fw={500} fz="lg">
                Topics
              </Tabs.Tab>
              {showNotifications && (
                <Tabs.Tab value={TabKeys.notifications} fw={500} fz="lg">
                  Notifications
                </Tabs.Tab>
              )}
            </Tabs.List>
          </Tabs>
        )}

        {/* Tab panels - shown for both mobile and desktop */}
        <div>
          {activeTab === TabKeys.topics && (
            <TopicsPanel
              showTutorialText={true}
              showModuleCounts
              registerUnsavedChangesChecker={registerUnsavedChangesChecker}
            />
          )}

          {activeTab === TabKeys.terminology && (
            <BrandingPanel registerUnsavedChangesChecker={registerUnsavedChangesChecker} />
          )}

          {activeTab === TabKeys.sidekicks && (
            <EditSidekicksPanel showTutorialText={true} registerUnsavedChangesChecker={registerUnsavedChangesChecker} />
          )}

          {activeTab === TabKeys.factFields && (
            <FactFieldsPanel showTutorialText={true} registerUnsavedChangesChecker={registerUnsavedChangesChecker} />
          )}

          {activeTab === TabKeys.generalInformation && (
            <GeneralInfoPanel showTutorialText={true} registerUnsavedChangesChecker={registerUnsavedChangesChecker} />
          )}

          {activeTab === TabKeys.draftWelcomeMessage && (
            <DraftWelcomeMessagePanel
              showTutorialText={true}
              registerUnsavedChangesChecker={registerUnsavedChangesChecker}
            />
          )}

          {activeTab === TabKeys.notifications && showNotifications && <NotificationsPanel />}
        </div>
      </Stack>
    </Screen>
  )
})
